<script setup lang="ts">
import type { DropdownMenuItem } from '@nuxt/ui'
import type { ReviewResponse } from '@/types'

const props = defineProps<{
  review: ReviewResponse
  isOwn?: boolean
  showActions?: boolean
}>()

const emit = defineEmits<{
  edit: [review: ReviewResponse]
  delete: [review: ReviewResponse]
}>()

function formatDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString('cs-CZ', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

function getUserInitials(name: string | null): string {
  if (!name) return '?'
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

function parseItems(text: string): string[] {
  return text.split('\n').map((s) => s.trim()).filter(Boolean)
}

const actionItems = computed<DropdownMenuItem[][]>(() => [
  [
    {
      label: 'Upravit',
      icon: 'i-lucide-pencil',
      onSelect: () => emit('edit', props.review),
    },
    {
      label: 'Smazat',
      icon: 'i-lucide-trash-2',
      color: 'error' as const,
      onSelect: () => emit('delete', props.review),
    },
  ],
])
</script>

<template>
  <div class="py-4 border-b border-default last:border-0">
    <div class="flex items-start gap-3">
      <UAvatar
        :text="getUserInitials(review.user.name)"
        size="sm"
        class="shrink-0"
      />

      <div class="flex-1 min-w-0">
        <!-- Header: name, date, actions -->
        <div class="flex items-center justify-between gap-2">
          <div class="flex items-center gap-2">
            <span class="font-medium text-highlighted">
              {{ review.user.name || 'Anonymní zákazník' }}
            </span>
            <span class="text-xs text-dimmed">
              {{ formatDate(review.createdAt) }}
            </span>
          </div>
          <!-- Actions for own reviews -->
          <ClientOnly>
            <UDropdownMenu v-if="showActions && isOwn" :items="actionItems">
              <UButton
                color="neutral"
                variant="ghost"
                size="xs"
                icon="i-lucide-more-vertical"
                aria-label="Akce"
              />
            </UDropdownMenu>
          </ClientOnly>
        </div>

        <!-- Rating on its own line -->
        <RatingStars v-if="review.rating" :rating="review.rating" size="sm" class="mt-1.5" />

        <!-- Comment -->
        <p v-if="review.comment" class="mt-2 text-muted italic">
          {{ review.comment }}
        </p>

        <!-- Pros & Cons two-column layout -->
        <div v-if="review.pros || review.cons" class="mt-3 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
          <!-- Pros (left) -->
          <div v-if="review.pros" class="space-y-1">
            <div
              v-for="(item, i) in parseItems(review.pros)"
              :key="`pro-${i}`"
              class="flex items-start gap-2"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor" class="text-green-500 shrink-0 mt-0.5"><circle cx="12" cy="12" r="10" /><line x1="8" y1="12" x2="16" y2="12" stroke="white" stroke-width="2" /><line x1="12" y1="8" x2="12" y2="16" stroke="white" stroke-width="2" /></svg>
              <span class="text-base text-muted">{{ item }}</span>
            </div>
          </div>

          <!-- Cons (right) -->
          <div v-if="review.cons" class="space-y-1">
            <div
              v-for="(item, i) in parseItems(review.cons)"
              :key="`con-${i}`"
              class="flex items-start gap-2"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor" class="text-red-500 shrink-0 mt-0.5"><circle cx="12" cy="12" r="10" /><line x1="8" y1="12" x2="16" y2="12" stroke="white" stroke-width="2" /></svg>
              <span class="text-base text-muted">{{ item }}</span>
            </div>
          </div>
        </div>

        <!-- Reply from clinic -->
        <div
          v-if="review.reply"
          class="mt-3 ml-4 p-3 bg-primary-50 rounded-lg border border-primary-100"
        >
          <div class="flex items-center gap-2 text-sm">
            <UBadge color="primary" variant="subtle" size="xs">
              Odpověď kliniky
            </UBadge>
            <span class="text-xs text-dimmed">
              {{ formatDate(review.reply.createdAt) }}
            </span>
          </div>
          <p class="mt-1.5 text-muted italic">
            {{ review.reply.content }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>
